import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, apiError, fmtDate, API } from "../lib/api";
import { toast } from "sonner";
import { FileText, Image as ImageIcon, Upload, ChevronLeft, RefreshCw, Trash2, Lock, Clock, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { t } from "../lib/i18n";

const CATEGORIES = [
    { key: "floor_plans", label_key: "docs_cat_floor_plans" },
    { key: "compliance", label_key: "docs_cat_compliance" },
    { key: "sops", label_key: "docs_cat_sops" },
    { key: "staff", label_key: "docs_cat_staff" },
];

/**
 * Fetches a protected document file with the Bearer token and returns a blob URL.
 * Revokes on unmount / doc change to avoid leaks. Fixes the iframe/img auth failure
 * where <iframe src="/api/documents/{id}/file"> couldn't forward the Authorization header.
 */
function useAuthedFile(docId) {
    const [state, setState] = useState({ url: null, loading: !!docId, error: null });
    useEffect(() => {
        if (!docId) { setState({ url: null, loading: false, error: null }); return; }
        let cancelled = false;
        let created = null;
        setState({ url: null, loading: true, error: null });
        (async () => {
            try {
                const res = await api.get(`/documents/${docId}/file`, { responseType: "blob" });
                if (cancelled) return;
                created = URL.createObjectURL(res.data);
                setState({ url: created, loading: false, error: null });
            } catch (e) {
                if (!cancelled) setState({ url: null, loading: false, error: apiError(e) });
            }
        })();
        return () => { cancelled = true; if (created) URL.revokeObjectURL(created); };
    }, [docId]);
    return state;
}

export default function DocumentsPage() {
    const { user } = useAuth();
    const { siteId } = useParams();
    const [sites, setSites] = useState([]);
    const [currentSiteId, setCurrentSiteId] = useState(siteId || "");
    const [category, setCategory] = useState("");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [viewer, setViewer] = useState(null);

    useEffect(() => {
        api.get("/sites").then(({ data }) => {
            setSites(data);
            if (!currentSiteId && data.length) setCurrentSiteId(data[0].id);
        });
    }, []);

    const load = useCallback(async () => {
        if (!currentSiteId) return;
        setLoading(true);
        try {
            const params = category ? { category } : {};
            const { data } = await api.get(`/sites/${currentSiteId}/documents`, { params });
            setRows(data);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }, [currentSiteId, category]);
    useEffect(() => { load(); }, [load]);

    const currentSite = sites.find((s) => s.id === currentSiteId);
    const isHOorPM = user?.role === "HO_ADMIN" || user?.role === "PROJECT_MANAGER";

    return (
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-6 sm:py-10">
            <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[#1E3355]"><ChevronLeft className="w-4 h-4" /> {t(user, "nav_sites")}</Link>
            <div className="mt-3 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500">{currentSite?.name || ""}</div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1 flex items-center gap-2">
                        <FileText className="w-6 h-6" /> {t(user, "docs_title")}
                    </h1>
                </div>
                <div className="flex items-center gap-2">
                    {sites.length > 1 && (
                        <select value={currentSiteId} onChange={(e) => setCurrentSiteId(e.target.value)} className="text-sm rounded-md border border-gray-300 bg-white px-3 py-2" data-testid="docs-site-select">
                            {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                    )}
                    <button onClick={() => setUploading(true)} className="inline-flex items-center gap-1.5 bg-[#1E3355] hover:bg-[#15243C] text-white text-sm font-semibold px-4 py-2 rounded-md" data-testid="docs-upload-btn"><Upload className="w-4 h-4" /> {t(user, "docs_upload")}</button>
                </div>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
                <button onClick={() => setCategory("")} className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${!category ? "bg-[#1E3355] text-white border-[#1E3355]" : "bg-white text-gray-700 border-gray-200"}`} data-testid="docs-cat-all">{t(user, "docs_all")}</button>
                {CATEGORIES.map((c) => (
                    <button key={c.key} onClick={() => setCategory(c.key)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${category === c.key ? "bg-[#1E3355] text-white border-[#1E3355]" : "bg-white text-gray-700 border-gray-200"}`} data-testid={`docs-cat-${c.key}`}>{t(user, c.label_key)}</button>
                ))}
            </div>

            {loading ? <div className="mt-6 h-40 rounded-xl bg-white border border-gray-100 animate-pulse" /> :
            rows.length === 0 ? <div className="mt-6 rounded-xl bg-white border border-gray-100 p-10 text-center text-sm text-gray-500" data-testid="docs-empty">No documents yet.</div> :
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="docs-grid">
                {rows.map((d) => <DocCard key={d.id} user={user} d={d} onOpen={() => setViewer(d)} onChanged={load} isHOorPM={isHOorPM} />)}
            </div>}

            {uploading && <UploadModal user={user} siteId={currentSiteId} onClose={() => setUploading(false)} onDone={() => { setUploading(false); load(); }} />}
            {viewer && <DocViewer user={user} doc={viewer} onClose={() => setViewer(null)} />}
        </div>
    );
}

function DocCard({ user, d, onOpen, onChanged, isHOorPM }) {
    const isImage = d.content_type?.startsWith("image/");
    // Fetch tiny authed thumbnail for images so the card preview loads for manager_ho docs too.
    const thumb = useAuthedFile(isImage ? d.id : null);
    const chip = d.expiry_status === "OVERDUE" ? "bg-red-100 text-[#E02B2B] border-red-200" :
                 d.expiry_status === "DUE_SOON" ? "bg-amber-50 text-amber-700 border-amber-200" :
                 d.expiry_status === "OK" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "";
    async function del() {
        if (!window.confirm(`Delete "${d.title}"?`)) return;
        try { await api.delete(`/documents/${d.id}`); onChanged(); toast.success("Deleted"); }
        catch (e) { toast.error(apiError(e)); }
    }
    return (
        <div className="rounded-xl bg-white border border-gray-100 fx-shadow-card overflow-hidden" data-testid={`doc-card-${d.id}`}>
            <button onClick={onOpen} className="w-full text-left" data-testid={`doc-open-${d.id}`}>
                <div className="aspect-[4/3] bg-[#F4F6F9] flex items-center justify-center overflow-hidden">
                    {isImage && thumb.url ? <img src={thumb.url} alt="" className="w-full h-full object-cover" /> :
                     isImage && thumb.loading ? <Loader2 className="w-8 h-8 text-gray-300 animate-spin" /> :
                     <FileText className="w-16 h-16 text-gray-300" />}
                </div>
                <div className="p-3">
                    <div className="flex items-center gap-1.5 flex-wrap mb-1">
                        <span className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{d.category.replace("_", " ")}</span>
                        {d.access_level === "manager_ho" && <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase text-[#E02B2B]"><Lock className="w-2.5 h-2.5" /> Manager+HO</span>}
                        {d.expiry_status && <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold border ${chip}`} data-testid={`doc-expiry-${d.id}`}><Clock className="w-2.5 h-2.5" /> {d.expiry_status === "OVERDUE" ? `${-d.days_to_due}d overdue` : d.expiry_status === "DUE_SOON" ? `Due in ${d.days_to_due}d` : "OK"}</span>}
                    </div>
                    <div className="font-display font-semibold text-[#1E3355] text-sm truncate">{d.title}</div>
                    <div className="text-[11px] text-gray-500 mt-0.5">v{d.version} · {fmtDate(d.created_at)} · {d.uploader_name}</div>
                </div>
            </button>
            {isHOorPM && (
                <div className="border-t border-gray-100 grid grid-cols-2 divide-x divide-gray-100 bg-[#FAFBFC]">
                    <button onClick={onOpen} className="py-2 text-xs font-semibold text-[#1E3355] hover:bg-[#F4F6F9] inline-flex items-center justify-center gap-1"><FileText className="w-3.5 h-3.5" /> {t(user, "docs_open")}</button>
                    <button onClick={del} className="py-2 text-xs font-semibold text-[#E02B2B] hover:bg-red-50 inline-flex items-center justify-center gap-1" data-testid={`doc-delete-${d.id}`}><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                </div>
            )}
        </div>
    );
}

function UploadModal({ user, siteId, onClose, onDone }) {
    const [form, setForm] = useState({ title: "", category: "floor_plans", access_level: "staff_visible", expiry_date: "" });
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    async function submit(e) {
        e.preventDefault();
        if (!file) { toast.error("Choose a file"); return; }
        const fd = new FormData();
        fd.append("title", form.title);
        fd.append("category", form.category);
        fd.append("access_level", form.access_level);
        if (form.expiry_date) fd.append("expiry_date", form.expiry_date);
        fd.append("file", file);
        setBusy(true);
        try {
            const backend = process.env.REACT_APP_BACKEND_URL || "";
            const token = localStorage.getItem("token");
            const res = await fetch(`${backend}/api/sites/${siteId}/documents`, { method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || `HTTP ${res.status}`); }
            toast.success("Uploaded");
            onDone();
        } catch (e) { toast.error(String(e.message || e)); }
        finally { setBusy(false); }
    }
    return (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="docs-upload-modal">
            <form onSubmit={submit} className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto">
                <div className="p-5 border-b border-gray-100">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-[#E02B2B]">Vault</div>
                    <div className="font-display font-semibold text-[#1E3355] mt-0.5">{t(user, "docs_upload")}</div>
                </div>
                <div className="p-5 space-y-3">
                    <Field label="Title"><input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={fieldCls} data-testid="docs-form-title" /></Field>
                    <Field label="Category"><select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={fieldCls} data-testid="docs-form-category">
                        {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.key.replace("_", " ")}</option>)}
                    </select></Field>
                    <Field label="Access level"><select value={form.access_level} onChange={(e) => setForm({ ...form, access_level: e.target.value })} className={fieldCls} data-testid="docs-form-access">
                        <option value="staff_visible">Staff visible</option>
                        <option value="manager_ho">Manager + HO only</option>
                    </select></Field>
                    <Field label="Expiry date (optional)"><input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} className={fieldCls} data-testid="docs-form-expiry" /></Field>
                    <Field label="File (PDF, JPEG, PNG, WebP · max 10 MB)"><input required type="file" accept="application/pdf,image/jpeg,image/png,image/webp" onChange={(e) => setFile(e.target.files?.[0] || null)} className="w-full text-sm" data-testid="docs-form-file" /></Field>
                </div>
                <div className="flex border-t border-gray-100">
                    <button type="button" onClick={onClose} className="flex-1 py-3 text-sm text-gray-600 hover:bg-gray-50">{t(user, "cancel")}</button>
                    <button type="submit" disabled={busy} className="flex-1 py-3 text-sm font-semibold text-white bg-[#1E3355] hover:bg-[#15243C] disabled:opacity-60" data-testid="docs-form-submit">{t(user, "submit")}</button>
                </div>
            </form>
        </div>
    );
}

function DocViewer({ user, doc, onClose }) {
    const { url, loading, error } = useAuthedFile(doc.id);
    const isImg = doc.content_type?.startsWith("image/");
    const isPdf = doc.content_type === "application/pdf";
    const isHO = user?.role === "HO_ADMIN";
    const [versions, setVersions] = useState(null);

    useEffect(() => {
        if (!isHO) return;
        api.get(`/documents/${doc.id}/versions`).then(({ data }) => setVersions(data)).catch(() => setVersions([]));
    }, [doc.id, isHO]);

    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="docs-viewer">
            <div className="max-w-5xl w-full max-h-full flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="bg-[#1E3355] text-white px-4 py-3 flex items-center justify-between rounded-t-lg">
                    <div className="min-w-0">
                        <div className="font-display font-semibold truncate">{doc.title}</div>
                        <div className="text-[10px] text-white/60 uppercase tracking-wider">v{doc.version} · {doc.category.replace("_", " ")} · {(doc.size_bytes / 1024).toFixed(0)} KB</div>
                    </div>
                    <div className="flex items-center gap-2">
                        {url && (
                            <a href={url} download={doc.filename} className="text-xs px-2.5 py-1 rounded-md bg-white/10 hover:bg-white/20 text-white font-semibold" data-testid="docs-viewer-download">Download</a>
                        )}
                        <button onClick={onClose} className="text-white/70 hover:text-white text-xl" data-testid="docs-viewer-close">×</button>
                    </div>
                </div>
                <div className="bg-white rounded-b-lg overflow-auto flex-1 min-h-[50vh] flex items-center justify-center">
                    {loading ? (
                        <div className="text-sm text-gray-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {t(user, "loading")}</div>
                    ) : error ? (
                        <div className="text-sm text-[#E02B2B] p-6" data-testid="docs-viewer-error">{error}</div>
                    ) : url && isImg ? (
                        <img src={url} alt={doc.title} className="max-w-full max-h-[80vh]" data-testid="docs-viewer-image" />
                    ) : url && isPdf ? (
                        <iframe src={url} title={doc.title} className="w-full h-[80vh] border-0" data-testid="docs-viewer-iframe" />
                    ) : url ? (
                        <div className="p-8 text-center">
                            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-3" />
                            <div className="text-sm text-gray-600 mb-3">Preview not available — download to view.</div>
                            <a href={url} download={doc.filename} className="inline-block px-4 py-2 rounded-md bg-[#1E3355] text-white text-sm font-semibold">Download {doc.filename}</a>
                        </div>
                    ) : null}
                </div>
                {isHO && versions && versions.length > 1 && (
                    <div className="mt-2 bg-white rounded-lg p-3 max-h-40 overflow-y-auto" data-testid="docs-viewer-versions">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-2">Version history ({versions.length})</div>
                        <ul className="space-y-1">
                            {versions.map((v) => (
                                <li key={v.id} className="flex items-center justify-between text-xs">
                                    <span><span className="font-mono text-[#1E3355]">v{v.version}</span> · {fmtDate(v.created_at)} · {v.uploader_name}</span>
                                    <VersionDownload docId={v.id} filename={v.filename} isCurrent={v.id === doc.id} />
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
}

function VersionDownload({ docId, filename, isCurrent }) {
    const { url, loading } = useAuthedFile(docId);
    if (isCurrent) return <span className="text-[10px] font-semibold text-emerald-700 uppercase">current</span>;
    if (loading) return <Loader2 className="w-3 h-3 animate-spin text-gray-400" />;
    if (!url) return <span className="text-[10px] text-gray-400">—</span>;
    return <a href={url} download={filename} className="text-[10px] font-semibold text-[#1E3355] hover:underline" data-testid={`version-download-${docId}`}>Download</a>;
}

const fieldCls = "w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1E3355] focus:ring-1 focus:ring-[#1E3355] bg-white";
function Field({ label, children }) { return (<div><label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>{children}</div>); }
